import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Table, Checkbox, Tag, Tooltip, Select, Skeleton, Button, Dropdown, Menu, notification } from "antd"
import { DownloadOutlined, EditOutlined } from "@ant-design/icons"
import { Link, useHistory } from "react-router-dom"
import moment from "moment"

import ColumnHeaderTitle from "../../../components/utilityComponents/table/ColumnHeaderTitle"
import ColumnSearchFilter from "../../../components/utilityComponents/table/ColumnSearchFilter"
import ColumnDateFilter from "../../../components/utilityComponents/table/ColumnDateFilter"
import WarningChip from "../../bills/components/subcomponents/WarningChip"
import FindingBanner from "./FindingBanner"
import { shiftRangeIds } from "./shiftRange"
import { tableFiltersApplied } from "../../../functions/objectFunctions/tableFiltersApplied"
import { formatCurrency } from "../../../lib/utilities"
import {
    fetchRunBills,
    filterRunBills,
    selectPendingBillIds,
    selectRunBills,
    selectRunBillsFilters,
    selectRunBillsLoading,
    selectRunBillsPagination,
    selectRunBillsSort,
    selectRunBillsState,
    selectRunBillsTotal,
    selectRunDetail,
    selectRunExcludedBillIds,
    setRunBillsFilters,
    setRunBillsSort,
    setRunBillsStateFilter,
    toggleBillExclusion,
    applyPreflightFilter,
    selectPreflightFilter,
    selectPreflightFindingPosition,
    setPreflightFilter,
    recheckFlags,
    flagRecheckSummary,
} from "../clientBatchingSlice"

const STATES = [
    { value: "all", label: "All bills" },
    { value: "included", label: "Included" },
    { value: "excluded", label: "Excluded" },
    { value: "withheld", label: "Withheld" },
    { value: "red_flagged", label: "Red-flagged" },
]

// mode="flagged": the Flagged tab -- fixed to the run's red-flagged bills, no state picker, and a
// Re-check link that moves bills whose flag has cleared back into the run.
export default function RunBillsTable({ mode }) {
    const flaggedMode = mode === "flagged"
    const dispatch = useDispatch()
    const history = useHistory()
    const findingPosition = useSelector(selectPreflightFindingPosition)
    const bills = useSelector(selectRunBills)
    const total = useSelector(selectRunBillsTotal)
    const run = useSelector(selectRunDetail)
    const excludedIds = useSelector(selectRunExcludedBillIds)
    const pendingIds = useSelector(selectPendingBillIds)
    const filters = useSelector(selectRunBillsFilters)
    const sort = useSelector(selectRunBillsSort)
    const pagination = useSelector(selectRunBillsPagination)
    const billState = useSelector(selectRunBillsState)
    const loading = useSelector(selectRunBillsLoading)

    const handleRecheckFlags = async () => {
        if (!run) return
        const result = await dispatch(recheckFlags(run.id))
        if (result.error) {
            notification.error({ message: "Red flags could not be re-checked. Please try again." })
            return
        }
        dispatch(fetchRunBills(run.id, { state: "red_flagged" }))
        notification.success({
            message: "Red flags re-checked",
            description: flagRecheckSummary(result) || "Nothing changed: these bills are still red-flagged.",
        })
    }

    // setPreflightFilter, not applyPreflightFilter(null): no point re-fetching this grid on the way
    // out. Pre-Flight re-checks on arrival, so it always shows the current findings.
    const handleBackToPreflight = () => {
        dispatch(setPreflightFilter(null))
        if (run) history.push(`/client_batching/runs/${run.id}/preflight`)
    }
    const preflightFilter = useSelector(selectPreflightFilter)

    // Shift-click: the last row whose checkbox was clicked. Reset whenever a different set of rows
    // is on screen -- but NOT on `bills`, which every toggle replaces with a new array.
    const [anchorId, setAnchorId] = useState(null)
    useEffect(() => {
        setAnchorId(null)
    }, [pagination.current, pagination.pageSize, filters, sort, billState, preflightFilter])

    const finalized = run && run.finalized

    const handleSortChange = (column) => () => {
        const direction = sort.column === column && sort.direction === "asc" ? "desc" : "asc"
        dispatch(filterRunBills({ filters, sort: { column, direction }, pagination }))
    }

    const handleTableChange = (newPagination) => {
        dispatch(filterRunBills({ filters, sort, pagination: newPagination }))
    }

    const renderColumnHeaderTitle = (title, altTitle = null) => (
        <ColumnHeaderTitle title={title} altTitle={altTitle} handleSortChange={handleSortChange} sort={sort} />
    )

    const renderSearchOutlinedButton = (filterKey) => (
        <span className={tableFiltersApplied(filterKey, filters) ? "filter-applied" : ""}>⌕</span>
    )

    const searchFilter = (column, filterType) => ({ visible }) => (
        <ColumnSearchFilter
            handleFetchInitial={() => dispatch(fetchRunBills(null))}
            filterAction={filterRunBills}
            filterColumn={column}
            filterType={filterType}
            filters={filters}
            pagination={pagination}
            setFilters={setRunBillsFilters}
            sort={sort}
            setSort={setRunBillsSort}
            visible={visible}
            useDispatch
        />
    )

    const dateFilter = (column) => ({ visible }) => (
        <ColumnDateFilter
            handleFetchInitial={() => dispatch(fetchRunBills(null))}
            filterAction={filterRunBills}
            filterColumn={column}
            filterType={column}
            filters={filters}
            pagination={pagination}
            setFilters={setRunBillsFilters}
            sort={sort}
            setSort={setRunBillsSort}
            visible={visible}
            useDispatch
        />
    )

    const handleToggle = async (billIds, excluded) => {
        const response = await dispatch(toggleBillExclusion(billIds, excluded))
        if (response && response.error) {
            const data = response.error.response && response.error.response.data
            notification.error({
                message: "Could not update this bill",
                description: data && data.message ? data.message.join(" ") : "Please try again.",
            })
        }
    }

    // A plain click toggles one row. A shift-click sets every toggleable row between the last clicked
    // row and this one to this row's new state, in one request (same endpoint as select-all).
    const handleRowToggle = (record, checked, shiftKey) => {
        const excluded = !checked
        const range = shiftKey && anchorId !== null ? shiftRangeIds(bills, anchorId, record.id, pendingIds) : []
        setAnchorId(record.id)
        handleToggle(range.length > 0 ? range : record.id, excluded)
    }

    // Controlled, and page scoped: antd only renders the current page. Withheld bills have no
    // checkbox, so they are not part of the count.
    const excludableOnPage = bills.filter((bill) => bill.excludable)
    const allIncluded =
        excludableOnPage.length > 0 && excludableOnPage.every((bill) => !excludedIds.includes(bill.id))

    const renderStateTag = (record) => {
        if (record.red_flagged) {
            return (
                <Tooltip
                    title={`${record.red_flag_message || "Current charges do not add up."} Held out of the run until the flag clears.`}
                >
                    <Tag color="red">Red flag</Tag>
                </Tooltip>
            )
        }
        if (record.withheld) {
            return (
                <Tooltip title="This bill's division has no batch group, so the sweep held it back. Assign a batch group to include it.">
                    <Tag color="orange">Withheld</Tag>
                </Tooltip>
            )
        }
        if (excludedIds.includes(record.id)) return <Tag>Excluded</Tag>
        return null
    }

    // Bill#file_url is an ARRAY. The other grids in this app pass it straight to window.open,
    // which coerces it to a comma joined string and breaks whenever a bill has two files.
    const renderCopy = (record) => {
        if (!record.file_count) return null
        if (record.file_count === 1) {
            return (
                <Button type="primary" size="small" onClick={() => window.open(record.file_urls[0], "_blank")}>
                    <DownloadOutlined />
                </Button>
            )
        }
        const menu = (
            <Menu>
                {record.file_urls.map((url, index) => (
                    <Menu.Item key={url} onClick={() => window.open(url, "_blank")}>
                        File {index + 1}
                    </Menu.Item>
                ))}
            </Menu>
        )
        return (
            <Dropdown overlay={menu}>
                <Button type="primary" size="small">
                    <DownloadOutlined /> {record.file_count}
                </Button>
            </Dropdown>
        )
    }

    return (
        <div className="muc-table card">
            <div className="card-body">
                {/* .card-body is a flex row with `> div { width: 100% }` -- everything must sit in
                    one wrapper or the heading and the table lay out side by side. */}
                <div>
                    {flaggedMode && (
                        <p className="p-10">
                            These bills' current charges do not add up to their charge lines, so they are held out of
                            the run. Fix a bill with the pencil, then Re-check: once its flag clears it moves back
                            into the run.
                        </p>
                    )}

                    <FindingBanner
                        filter={preflightFilter}
                        total={total}
                        position={findingPosition}
                        onBack={handleBackToPreflight}
                        onNext={() => dispatch(applyPreflightFilter(findingPosition.next))}
                        onClear={() => dispatch(applyPreflightFilter(null))}
                    />

                    <div className="card-body-heading">
                        <div className="badged-header">
                            <h2>{flaggedMode ? "Red-flagged bills" : "Bills"}</h2>
                            <div className="badge">{total}</div>
                        </div>

                        <div className="actions">
                            <div className="table-action-links">
                                {flaggedMode ? (
                                    !finalized && (
                                        <a href="javascript:;" onClick={handleRecheckFlags}>
                                            Re-check
                                        </a>
                                    )
                                ) : (
                                    <Select
                                        value={billState}
                                        onChange={(value) => dispatch(setRunBillsStateFilter(value))}
                                        options={STATES}
                                        style={{ width: 150 }}
                                        size="small"
                                    />
                                )}
                            </div>
                        </div>
                    </div>

                    <Skeleton active loading={loading && bills.length === 0}>
                        <Table
                            dataSource={bills}
                            rowKey={(r) => r.id}
                            // A finding someone has already fixed has no bills left behind it.
                            locale={
                                flaggedMode
                                    ? { emptyText: "No red-flagged bills — everything in this run can be batched." }
                                    : preflightFilter
                                    ? { emptyText: "No bills left for this finding — it may already be fixed. Use Update to re-check." }
                                    : undefined
                            }
                            loading={loading}
                            onChange={handleTableChange}
                            pagination={{
                                ...pagination,
                                total: total,
                                showSizeChanger: true,
                                pageSizeOptions: [25, 50, 100, 200],
                            }}
                        >
                            <Table.Column
                                key="in"
                                dataIndex="in"
                                width={50}
                                title={
                                    <Checkbox
                                        checked={allIncluded}
                                        disabled={finalized || excludableOnPage.length === 0}
                                        onChange={(e) =>
                                            handleToggle(
                                                excludableOnPage.map((bill) => bill.id),
                                                !e.target.checked
                                            )
                                        }
                                    />
                                }
                                render={(text, record) =>
                                    record.excludable ? (
                                        <Checkbox
                                            checked={!excludedIds.includes(record.id)}
                                            disabled={pendingIds.includes(record.id) || finalized}
                                            onChange={(e) =>
                                                handleRowToggle(record, e.target.checked, !!(e.nativeEvent && e.nativeEvent.shiftKey))
                                            }
                                        />
                                    ) : null
                                }
                            />
                            <Table.Column
                                title={
                                    <Tooltip title="Sorts by client, then batch group">
                                        {renderColumnHeaderTitle("client_name", "Client")}
                                    </Tooltip>
                                }
                                key="client_name"
                                dataIndex="client_name"
                                filterDropdown={searchFilter("client_name", "client_name_cont")}
                                filterIcon={() => renderSearchOutlinedButton("client_name_cont")}
                            />
                            <Table.Column
                                title={
                                    <Tooltip title="Sorts by batch group, then client">
                                        {renderColumnHeaderTitle("batch_group_name", "Batch Group")}
                                    </Tooltip>
                                }
                                key="batch_group_name"
                                dataIndex="batch_group_name"
                                filterDropdown={searchFilter("batch_group_name", "batch_group_name_cont")}
                                filterIcon={() => renderSearchOutlinedButton("batch_group_name_cont")}
                                render={(text) => text || "No batch group"}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("division_name", "Division")}
                                key="division_name"
                                dataIndex="division_name"
                                filterDropdown={searchFilter("division_name", "division_name_cont")}
                                filterIcon={() => renderSearchOutlinedButton("division_name_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("account_number", "Account")}
                                key="account_number"
                                dataIndex="account_name_with_number"
                                filterDropdown={searchFilter("account_number", "account_number_cont")}
                                filterIcon={() => renderSearchOutlinedButton("account_number_cont")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("vendor_name", "Vendor")}
                                key="vendor_name"
                                dataIndex="vendor_name"
                                filterDropdown={searchFilter("vendor_name", "vendor_name_cont")}
                                filterIcon={() => renderSearchOutlinedButton("vendor_name_cont")}
                            />
                            {/* Read-only, for information (Georges, A4). Same text the per-client
                                Client Batch screen shows, but deliberately NOT its link to the
                                payment's edit page -- the review does not change payment data. */}
                            <Table.Column
                                title="Vendor Payment"
                                key="vendor_payment_name"
                                dataIndex="vendor_payment_name"
                                render={(text) => text || ""}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("date", "Bill Date")}
                                key="date"
                                dataIndex="date"
                                filterDropdown={dateFilter("date")}
                                render={(text) => (text ? moment(text).format("L") : "")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("due_date", "Due Date")}
                                key="due_date"
                                dataIndex="due_date"
                                filterDropdown={dateFilter("due_date")}
                                render={(text) => (text ? moment(text).format("L") : "")}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("current_charges", "Current Charges")}
                                key="current_charges"
                                dataIndex="current_charges"
                                align="right"
                                render={(text) => formatCurrency(text)}
                            />
                            <Table.Column
                                title={renderColumnHeaderTitle("amount_paid", "Amount Paid")}
                                key="amount_paid"
                                dataIndex="amount_paid"
                                align="right"
                                render={(text) => (text === null || text === undefined ? "" : formatCurrency(text))}
                            />
                            <Table.Column
                                title={
                                    <Tooltip title="What this bill contributes to the client's batch file. Supply bills read zero: their charges are counted on the linked billable bill. Not sortable.">
                                        Client Batch Cost
                                    </Tooltip>
                                }
                                key="client_batch_cost"
                                dataIndex="client_batch_cost"
                                align="right"
                                render={(text, record) =>
                                    record.supply_cost_on_linked_bill && Number(text) === 0 ? (
                                        <Tooltip title="Counted on the linked billable bill">
                                            <span style={{ opacity: 0.5 }}>{formatCurrency(0)}</span>
                                        </Tooltip>
                                    ) : (
                                        formatCurrency(text)
                                    )
                                }
                            />
                            <Table.Column
                                title="Status"
                                key="flags"
                                dataIndex="flags"
                                width={70}
                                render={(flags, record) => (
                                    <WarningChip flags={flags} audience="external" childFlags={record.any_child_flags} />
                                )}
                            />
                            <Table.Column title="Copy" key="copy" render={(text, record) => renderCopy(record)} />
                            <Table.Column title="" key="state" render={(text, record) => renderStateTag(record)} />
                            {/* Same link as the Client Batches bill table. A new tab, so the run keeps its page,
                                filters and selections while the bill is edited. */}
                            <Table.Column
                                title="Edit"
                                key="edit"
                                render={(text, record) => (
                                    <Link to={`/clients/${record.client_id}/bills/${record.id}/edit`} target="_blank">
                                        <Tooltip title="Edit">
                                            <EditOutlined />
                                        </Tooltip>
                                    </Link>
                                )}
                            />
                        </Table>
                    </Skeleton>
                </div>
            </div>
        </div>
    )
}
